import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Badge, Button, Card, Col, Row, Spinner } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import DataTable from 'react-data-table-component'
import { FaArrowsRotate, FaFileSignature } from 'react-icons/fa6'
import NotificationService from '../../services/notification/NotificationService'

const PAGE_SIZE = 10
const FETCH_SIZE = 50
const MAX_NOTIFICATION_PAGES = 10

const formatDate = value => value
    ? new Date(value).toLocaleString('id-ID', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    })
    : '-'

const isSignedDocument = item =>
    item?.type === 'SIGNATURE_SUCCESS' ||
    item?.type === 'DOCUMENT_SIGNED' ||
    item?.action === 'OPEN_SIGNED_DOCUMENT'

export default function SignedDocumentsPage() {
    const navigate = useNavigate()
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(PAGE_SIZE)

    let notificationService = useMemo(() => new NotificationService(), [])

    const load = useCallback(async () => {
        setLoading(true)
        setError('')

        try {
            const signedItems = []

            for (let notificationPage = 0; notificationPage < MAX_NOTIFICATION_PAGES; notificationPage += 1) {
                const response = await notificationService.list(false, notificationPage, FETCH_SIZE)
                const data = response.data || {}
                const content = Array.isArray(data) ? data : (data.content || [])

                signedItems.push(...content.filter(isSignedDocument))

                if (Array.isArray(data)) {
                    if (content.length < FETCH_SIZE) break
                } else if (data.last || content.length < FETCH_SIZE) {
                    break
                }
            }

            const uniqueItems = Array.from(
                new Map(
                    signedItems
                        .filter(item => item.referenceId)
                        .map(item => [item.referenceId, item])
                ).values()
            )

            uniqueItems.sort((a, b) => {
                const da = new Date(a.createdAt || a.createdDate || 0).getTime()
                const db = new Date(b.createdAt || b.createdDate || 0).getTime()
                return db - da
            })

            setItems(uniqueItems)
            setPage(1)
        } catch (e) {
            setError('Daftar dokumen yang sudah ditandatangani tidak dapat dimuat.')
        } finally {
            setLoading(false)
        }
    }, [notificationService])

    useEffect(() => {
        load()
    }, [load])

    const columns = [
        {
            name: 'Waktu TTE',
            selector: row => row.createdAt || row.createdDate || '',
            sortable: true,
            width: '180px',
            cell: row => (
                <span className="text-nowrap">
                    {formatDate(row.createdAt || row.createdDate)}
                </span>
            )
        },
        {
            name: 'Dokumen',
            grow: 2,
            cell: row => (
                <div className="py-2">
                    <div className="fw-semibold">{row.title || 'Dokumen SMKHP'}</div>
                    <div className="small text-muted">{row.message || '-'}</div>
                </div>
            )
        },
        {
            name: 'Tahap',
            width: '130px',
            cell: row => (
                <Badge bg="success">
                    <FaFileSignature className="me-1" />
                    {row.signatureStage || row.stage || 'TTE'}
                </Badge>
            )
        },
        {
            name: 'Aksi',
            width: '160px',
            cell: row => (
                <Button
                    size="sm"
                    variant="success"
                    onClick={() => navigate(`/signature/${row.referenceId}/signed`)}
                >
                    Lihat Dokumen
                </Button>
            )
        }
    ]

    return (
        <div className="container-fluid py-4">
            <Card className="mb-4 bg-transparent border-0 shadow-none">
                <Card.Body className="px-1 py-0">
                    <Row className="align-items-end">
                        <Col md={8}>
                            <h3 className="text-white fw-bold mb-2">Dokumen TTE</h3>
                            <div className="text-white">
                                Daftar dokumen yang sudah selesai ditandatangani dan dapat dilihat kembali.
                            </div>
                        </Col>
                        <Col md={4} className="d-flex justify-content-md-end mt-3 mt-md-0">
                            <Button
                                variant="warning"
                                size="sm"
                                disabled={loading}
                                onClick={load}
                            >
                                <FaArrowsRotate className="me-1" /> Refresh
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    {loading && (
                        <div className="text-center py-5">
                            <Spinner animation="border" />
                            <div className="small text-muted mt-2">Memuat dokumen TTE...</div>
                        </div>
                    )}

                    {!loading && error && (
                        <div className="alert alert-danger m-3">{error}</div>
                    )}

                    {!loading && !error && (
                        <DataTable
                            columns={columns}
                            data={items}
                            responsive
                            highlightOnHover
                            noDataComponent={(
                                <div className="py-5 text-muted">
                                    Belum ada dokumen yang sudah ditandatangani.
                                </div>
                            )}
                            pagination
                            paginationPerPage={perPage}
                            paginationDefaultPage={page}
                            paginationRowsPerPageOptions={[10, 20, 50]}
                            onChangePage={nextPage => setPage(nextPage)}
                            onChangeRowsPerPage={(nextPerPage, nextPage) => {
                                setPerPage(nextPerPage)
                                setPage(nextPage)
                            }}
                        />
                    )}
                </Card.Body>
            </Card>
        </div>
    )
}
